/**
 * This file was generated by kysely-codegen.
 * Please do not edit it manually.
 */

import type { ColumnType } from 'kysely';

export type AiProviderType =
  | 'anthropic'
  | 'google'
  | 'nvidia_nim'
  | 'ollama_cloud'
  | 'ollama_local'
  | 'openai'
  | 'openrouter';

export type CheckpointType = 'auto' | 'manual' | 'milestone';

export type DeploymentStatus = 'deployed' | 'deploying' | 'failed' | 'pending';

export type Generated<T> =
  T extends ColumnType<infer S, infer I, infer U>
    ? ColumnType<S, I | undefined, U>
    : ColumnType<T, T | undefined, T>;

export type IntegrationProvider = 'atlassian' | 'github' | 'gitlab' | 'supabase';

export type Json = JsonValue;

export type JsonArray = JsonValue[];

export type JsonObject = {
  [x: string]: JsonValue | undefined;
};

export type JsonPrimitive = boolean | number | string | null;

export type JsonValue = JsonArray | JsonObject | JsonPrimitive;

export type PermissionLevel = 'edit' | 'none' | 'view';

export type QueuePriority = 'high' | 'low' | 'medium' | 'urgent';

export type AiTaskQueueStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';

export type SessionStatus = 'active' | 'archived' | 'draft';

export type Timestamp = ColumnType<Date, Date | string, Date | string>;

export type UserPresenceStatus = 'away' | 'meeting' | 'offline' | 'online';

export type UserRole = 'admin' | 'guest' | 'host';

export type SubscriptionTier = 'free' | 'basic' | 'pro' | 'enterprise';
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'inactive' | 'trialing';

// Multi-tenancy types
export type TenantType = 'personal' | 'team' | 'enterprise';
export type TenantUserRole = 'owner' | 'admin' | 'member' | 'viewer' | 'billing';
export type TenantAuthMode = 'password_only' | 'sso_required' | 'sso_optional';
export type PartnerTier = 'silver' | 'gold' | 'platinum';
export type AuditSeverity = 'info' | 'warn' | 'error' | 'critical';

// Lead/Sales pipeline types
export type LeadStatus = 'new' | 'contacted' | 'qualified' | 'meeting_booked' | 'proposal_sent' | 'negotiation' | 'won' | 'lost' | 'nurture';
export type LeadSource = 'linkedin' | 'website' | 'referral' | 'cold_outreach' | 'inbound' | 'event' | 'partner' | 'ai_discovered' | 'manual';
export type LeadPriority = 'hot' | 'warm' | 'cold';
export type OutreachChannel = 'email' | 'linkedin' | 'phone' | 'sms' | 'twitter';
export type OutreachStatus = 'draft' | 'scheduled' | 'sent' | 'opened' | 'replied' | 'bounced';

export interface AiChatMessages {
  checkpointId: string | null;
  content: string;
  createdAt: Generated<Timestamp | null>;
  fileContext: string[] | null;
  id: Generated<string>;
  imageAttachment: string | null; // JSON string with { content: base64, name: string }
  personaId: string | null;
  role: string;
  sessionId: string;
  userId: number;
}

export interface Annotations {
  annotationData: Json;
  attachmentId: string | null;
  checkpointId: string | null;
  createdAt: Generated<Timestamp | null>;
  createdBy: number;
  description: string | null;
  id: Generated<string>;
  sessionId: string;
  updatedAt: Generated<Timestamp | null>;
}

export interface Checkpoints {
  checkpointType: Generated<CheckpointType>;
  createdAt: Generated<Timestamp | null>;
  createdBy: number | null;
  description: string | null;
  filesSnapshot: Json;
  id: Generated<string>;
  name: string;
  sessionId: string;
}

export interface CollaborationSessions {
  aiCustomEndpoint: string | null;
  aiEnabled: Generated<boolean | null>;
  aiModel: Generated<string | null>;
  aiProvider: Generated<AiProviderType | null>;
  allowParticipantPersonas: Generated<boolean | null>;
  autoApplyRecommendations: Generated<boolean | null>; // Auto-apply high-priority recommendations in autonomous mode
  fullAutonomousMode: Generated<boolean | null>; // Execute tasks without confirmation, navigate autonomously
  skipCredentialChecks: Generated<boolean | null>; // Don't block tasks when inferred credentials are missing
  createdAt: Generated<Timestamp>;
  description: string | null;
  id: string;
  leadAiParticipantId: Generated<number | null>;
  name: string;
  ownerId: number | null;
  previewSubdomain: string | null;
  status: Generated<SessionStatus>;
  templateId: string | null;
  tenantId: string | null;
  updatedAt: Generated<Timestamp>;
}

export interface FilePermissions {
  createdAt: Generated<Timestamp>;
  fileId: string | null;
  filePath: string | null;
  id: Generated<number>;
  permissionLevel: Generated<PermissionLevel>;
  sessionId: string;
  updatedAt: Generated<Timestamp>;
  userId: number | null;
}

export interface LoginAttempts {
  attemptedAt: Timestamp | null;
  email: string;
  id: Generated<number>;
  success: Generated<boolean>;
}

export interface OauthAccounts {
  createdAt: Generated<Timestamp>;
  id: Generated<number>;
  provider: string;
  providerEmail: string;
  providerUserId: string;
  updatedAt: Generated<Timestamp>;
  userId: number;
}

export interface OauthStates {
  codeVerifier: string;
  createdAt: Generated<Timestamp>;
  expiresAt: Timestamp;
  id: Generated<number>;
  originDomain: string | null; // Original domain for cross-domain OAuth redirect
  provider: string;
  redirectUrl: string;
  ssoConfigId: string | null; // Per-domain SSO config ID for enterprise SSO
  state: string;
}

export interface PublishedSites {
  accessToken: string | null;
  accessTokenCreatedAt: Timestamp | null;
  cloudflareProjectName: string;
  createdAt: Generated<Timestamp | null>;
  customDomain: string | null;
  taskingTechSubdomain: string | null;
  d1DatabaseId: string | null;
  d1DatabaseName: string | null;
  deploymentStatus: Generated<DeploymentStatus>;
  deploymentUrl: string | null;
  hasDatabase: Generated<boolean | null>;
  id: Generated<number>;
  lastDeployedAt: Timestamp | null;
  sessionId: string;
  subdomain: string;
  updatedAt: Generated<Timestamp | null>;
  userId: number;
}

export interface SessionAttachments {
  contentBase64: string;
  createdAt: Generated<Timestamp | null>;
  fileName: string;
  fileSize: number;
  fileType: string;
  id: Generated<string>;
  sessionId: string;
  uploadedBy: number;
}

export interface SessionChatMessages {
  createdAt: Generated<Timestamp>;
  id: string;
  message: string;
  sessionId: string;
  userId: number;
  personaId: string | null;
}

export interface SessionFavorites {
  createdAt: Generated<Timestamp | null>;
  id: Generated<number>;
  sessionId: string;
  userId: number;
}

export interface SessionFiles {
  content: Generated<string>;
  createdAt: Generated<Timestamp>;
  fileName: string;
  filePath: string;
  id: string;
  language: Generated<string>;
  sessionId: string;
  updatedAt: Generated<Timestamp>;
}

export interface SessionInvitations {
  createdAt: Generated<Timestamp>;
  createdBy: number;
  email: string | null;
  expiresAt: Timestamp;
  id: string;
  inviteCode: string;
  permissionLevel: Generated<PermissionLevel>;
  requireIca: Generated<number>;
  sessionId: string;
  usedAt: Timestamp | null;
  usedBy: number | null;
}

export interface SessionParticipants {
  id: Generated<number>;
  invitedBy: number | null;
  joinedAt: Generated<Timestamp>;
  permissionLevel: Generated<PermissionLevel>;
  sessionId: string;
  userId: number;
}

export interface SessionPresence {
  currentFile: string | null;
  cursorColumn: Generated<number | null>;
  cursorLine: Generated<number | null>;
  id: Generated<number>;
  lastSeenAt: Generated<Timestamp | null>;
  selectionEndColumn: number | null;
  selectionEndLine: number | null;
  selectionStartColumn: number | null;
  selectionStartLine: number | null;
  sessionId: string;
  status: Generated<UserPresenceStatus | null>;
  userId: number;
}

export interface Sessions {
  createdAt: Generated<Timestamp>;
  expiresAt: Timestamp;
  id: string;
  lastAccessed: Generated<Timestamp>;
  userId: number;
}

export interface TaskQueues {
  assignedTo: number | null;
  assignedPersonaId: string | null;
  createdAt: Generated<Timestamp | null>;
  createdBy: number;
  description: string | null;
  fileId: string | null;
  id: Generated<string>;
  position: Generated<number>;
  priority: Generated<QueuePriority>;
  sessionId: string;
  status: Generated<string>;
  title: string;
  updatedAt: Generated<Timestamp | null>;
}

export interface TodoItems {
  createdAt: Generated<Timestamp | null>;
  description: string | null;
  dueDate: Timestamp | null;
  id: Generated<string>;
  isCompleted: Generated<boolean | null>;
  position: Generated<number | null>;
  sessionId: string;
  title: string;
  updatedAt: Generated<Timestamp | null>;
  userId: number;
  generationStatus: Generated<string | null>; // 'pending' | 'processing' | 'completed' | 'failed'
  generationResult: string | null; // AI-generated code/content
  suggestions: string | null; // JSON array of 4 suggestions
  ambiguous: Generated<number | null>; // 0 or 1 flag
  attempts: Generated<number | null>;
  generationProgressMessage: string | null;
  generationProgressPercent: Generated<number | null>;
}

export interface TodoSteps {
  createdAt: Generated<Timestamp | null>;
  id: Generated<string>;
  isCompleted: Generated<boolean | null>;
  position: Generated<number | null>;
  title: string;
  todoId: string;
}

export type LocalLlmType = 'ollama' | 'lm_studio' | 'llama_cpp' | 'openai_compatible';

export interface UserAiConfigs {
  createdAt: Generated<Timestamp | null>;
  customModels: Generated<Json | null>;
  id: Generated<number>;
  localLlmApiKey: string | null;
  localLlmDefaultModel: string | null;
  localLlmEnabled: Generated<boolean | null>;
  localLlmEndpoint: string | null;
  localLlmModels: Generated<string[] | null>;
  localLlmType: Generated<LocalLlmType | null>;
  preferredCloudModel: Generated<string | null>;
  preferredCloudModelApiKey: string;
  preferredCloudProvider: Generated<AiProviderType | null>;
  updatedAt: Generated<Timestamp | null>;
  userId: number;
}

export interface UserIntegrations {
  accessToken: string;
  createdAt: Generated<Timestamp | null>;
  id: Generated<number>;
  metadata: Json | null;
  provider: IntegrationProvider;
  providerUserId: string | null;
  providerUsername: string | null;
  refreshToken: string | null;
  scopes: string[] | null;
  tokenExpiresAt: Timestamp | null;
  updatedAt: Generated<Timestamp | null>;
  userId: number;
}

export interface UserPasswords {
  createdAt: Generated<Timestamp>;
  passwordHash: string;
  updatedAt: Generated<Timestamp>;
  userId: number;
}

/** Built-in categories — custom user categories are also allowed as any string */
export type PersonaCategory = string;

export type PersonaVisibility = 'private' | 'session' | 'public';

export type PersonaSource = 'manual' | 'imported' | 'ai_generated';

export interface AiPersonas {
  id: Generated<string>;
  name: string;
  description: string | null;
  icon: string;
  category: PersonaCategory;
  systemPrompt: string;
  isBuiltin: Generated<boolean>;
  createdBy: number | null;
  priority: Generated<number>;
  tags: Json | null;
  visibility: Generated<PersonaVisibility>;
  source: Generated<PersonaSource>;
  originalId: string | null;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

export interface UserPersonaSettings {
  id: Generated<number>;
  userId: number;
  personaId: string;
  isEnabled: Generated<boolean>;
  customPriority: number | null;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

export interface SessionPersonaSettings {
  id: Generated<number>;
  sessionId: string;
  personaId: string;
  enabledBy: number;
  isEnabled: Generated<boolean>;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

export interface PersonaImports {
  id: Generated<number>;
  userId: number;
  personaId: string;
  importSource: string | null;
  importData: string | null;
  createdAt: Generated<Timestamp | null>;
}

export interface SessionAiParticipants {
  id: Generated<number>;
  sessionId: string;
  personaId: string;
  addedBy: number | null;
  addedAt: Generated<Timestamp | null>;
  isActive: Generated<boolean>;
  quantity: Generated<number>;
  displayName: string | null;
  avatarUrl: string | null;
}

export interface AiTaskQueue {
  id: Generated<string>;
  taskId: string;
  sessionId: string;
  personaId: string;
  triggeredBy: number;
  status: Generated<AiTaskQueueStatus>;
  priority: Generated<number>;
  attempts: Generated<number>;
  maxAttempts: Generated<number>;
  errorMessage: string | null;
  startedAt: Timestamp | null;
  completedAt: Timestamp | null;
  resultChatMessageId: string | null;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

export type MemorySummaryTrigger = 'auto' | 'manual' | 'deploy' | 'refactor' | 'migration';

export interface SessionMemorySummaries {
  id: Generated<string>;
  sessionId: string;
  projectGoal: string | null;
  keyDecisions: Json | null;
  schemaNotes: Json | null;
  openTodos: Json | null;
  messageCountAtCreation: number;
  trigger: Generated<MemorySummaryTrigger>;
  createdBy: number;
  createdAt: Generated<Timestamp | null>;
}

export interface UserN8nIntegrations {
  apiKey: string;
  createdAt: Generated<Timestamp | null>;
  id: Generated<number>;
  instanceName: string | null;
  instanceUrl: string;
  updatedAt: Generated<Timestamp | null>;
  userId: number;
}

// n8n Workflow types
export type N8nWorkflowExecutionStatus = 'running' | 'success' | 'error' | 'cancelled';

export interface N8nWorkflows {
  id: string;
  userId: number;
  sessionId: string | null;
  name: string;
  description: string | null;
  nodes: string; // JSON array of workflow nodes
  connections: string; // JSON object of node connections
  settings: string | null; // JSON object of workflow settings
  tags: string | null; // JSON array of tags
  active: Generated<number>;
  n8nWorkflowId: string | null; // ID in the n8n instance (if synced)
  lastExecutedAt: Timestamp | null;
  executionCount: Generated<number>;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface N8nWorkflowExecutions {
  id: string;
  workflowId: string;
  userId: number;
  n8nExecutionId: string | null; // ID in n8n (if available)
  status: N8nWorkflowExecutionStatus;
  startedAt: Timestamp;
  finishedAt: Timestamp | null;
  duration: number | null; // milliseconds
  error: string | null;
  inputData: string | null; // JSON
  outputData: string | null; // JSON
}

export interface N8nUserCredentials {
  id: string;
  userId: number;
  name: string;
  type: string; // 'slack', 'github', 'openai', etc.
  n8nCredentialId: string | null; // ID in n8n instance
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface SessionSecrets {
  id: Generated<string>;
  sessionId: string;
  keyName: string;
  encryptedValue: string;
  description: string | null;
  createdBy: number;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

export interface PlatformSecrets {
  id: Generated<number>;
  keyName: string;
  encryptedValue: string;
  description: string | null;
  isOverridable: Generated<number>;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

export interface SessionThemes {
  id: Generated<string>;
  sessionId: string;
  name: string;
  description: string | null;
  isActive: Generated<boolean>;
  tokens: string; // JSON string containing theme tokens
  theme_mode: string | null; // 'light' | 'dark' | null (null = legacy single-mode)
  paired_theme_id: string | null; // ID of the linked light/dark variant
  imagePrompt: string | null;
  imageUrl: string | null;
  imageGeneratedAt: Timestamp | null;
  createdBy: number;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

/** Global user themes (not tied to a session). Stored in user_themes. */
export interface UserThemes {
  id: Generated<string>;
  userId: number;
  name: string;
  description: string | null;
  isActive: Generated<number>;
  tokens: string;
  themeMode: string; // 'light' | 'dark'
  pairedThemeId: string | null;
  imagePrompt: string | null;
  imageUrl: string | null;
  imageGeneratedAt: string | null;
  isPublic: Generated<number>;
  usageCount: Generated<number>;
  createdAt: Generated<string | null>;
  updatedAt: Generated<string | null>;
}

export interface GeneratedImages {
  id: Generated<string>;
  sessionId: string;
  prompt: string;
  model: Generated<string>;
  imageUrl: string;
  width: Generated<number>;
  height: Generated<number>;
  metadata: string | null; // JSON string
  createdBy: number;
  createdAt: Generated<Timestamp | null>;
}

export interface ChatAttachments {
  id: Generated<string>;
  sessionId: string;
  userId: number;
  fileName: string;
  fileType: string | null;
  fileSize: number | null;
  dataUrl: string;
  isPrivate: Generated<number>;
  shareToken: string | null;
  sharedWith: string | null;
  createdAt: Generated<Timestamp | null>;
}

export interface UserMedia {
  id: Generated<string>;
  userId: number;
  fileName: string;
  fileType: string | null;
  fileSize: number | null;
  url: string;
  thumbnailUrl: string | null;
  source: string;
  prompt: string | null;
  provider: string | null;
  r2Key: string | null;
  metadata: string | null;
  taskId: string | null;
  conversationId: string | null;
  folderId: string | null;
  createdAt: Generated<Timestamp | null>;
}

export type VoicePreference = 'female_british' | 'male_deep';
export type SupportedLanguageCode =
  | 'en'
  | 'zh-CN'
  | 'es'
  | 'fr'
  | 'de'
  | 'it'
  | 'pt-BR'
  | 'ja'
  | 'ko'
  | 'ar';

export interface Users {
  activeTenantId: string | null;
  avatarUrl: string | null;
  createdAt: Generated<Timestamp>;
  displayName: string;
  email: string;
  id: Generated<number>;
  role: Generated<UserRole>;
  updatedAt: Generated<Timestamp>;
  preferredLanguage: Generated<SupportedLanguageCode>;
  voicePreference: Generated<VoicePreference>;
  siteThemeId: string | null;
  totpSecret: string | null;
  totpEnabled: Generated<boolean>;
  totpBackupCodes: string | null; // JSON array of hashed backup codes
}

export type TaskingBotChannelType =
  | 'telegram'
  | 'whatsapp'
  | 'signal'
  | 'discord'
  | 'web'
  | 'voice';
export type TaskingBotChannelStatus = 'connected' | 'disconnected' | 'error';

export interface TaskingBotChannels {
  id: Generated<number>;
  userId: number;
  channelType: TaskingBotChannelType;
  enabled: Generated<boolean>;
  config: Generated<string>; // JSON: bot tokens, webhook URLs, phone numbers, etc.
  webhookUrl: string | null;
  status: Generated<TaskingBotChannelStatus>;
  lastError: string | null;
  lastActiveAt: Timestamp | null;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

// OpenClaw: TaskingBot task persistence (separate from AI chat taskQueues)
export type TaskingBotTaskStatus =
  | 'open'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'waiting_for_input';
export type TaskingBotTaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface TaskingBotCategories {
  id: Generated<string>;
  userId: number;
  name: string;
  parentId: string | null;
  position: Generated<number>;
  createdAt: Generated<Timestamp | null>;
}

export interface TaskingBotProjects {
  id: Generated<string>;
  userId: number;
  name: string;
  description: string | null;
  categoryId: string;
  parentProjectId: string | null;
  color: string | null;
  icon: string | null;
  position: Generated<number>;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

export interface TaskingBotTasks {
  id: Generated<string>;
  userId: number;
  title: string;
  description: string | null;
  status: Generated<TaskingBotTaskStatus>;
  priority: Generated<TaskingBotTaskPriority>;
  project: string | null;
  projectId: string | null;
  conversationId: string | null;
  startDate: string | null;
  startTime: string | null;
  endDate: string | null;
  endTime: string | null;
  dueDate: string | null;
  dueTime: string | null;
  cronExpression: string | null;
  cronTimezone: string | null;
  tags: Generated<string>; // JSON array
  metadata: Generated<string>; // JSON object
  position: Generated<number>;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
  completedAt: Timestamp | null;
}

// OpenClaw: TaskingBot per-task message threads
export interface TaskingBotTaskMessages {
  id: Generated<number>;
  taskId: string;
  userId: number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata: string | null; // JSON: tool results, status updates, etc.
  createdAt: Generated<Timestamp | null>;
}

export interface TaskingBotConversations {
  id: Generated<string>;
  userId: number;
  title: string;
  projectId: string | null;
  isPinned: Generated<number>;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

export interface TaskingBotChatMessages {
  id: Generated<number>;
  userId: number;
  role: 'user' | 'assistant';
  content: string;
  attachmentJson: string | null; // JSON: { name, type, content, size }
  conversationId: string | null;
  createdAt: Generated<Timestamp | null>;
}

// OpenClaw: TaskingBot memory system (soul/user/memory categories)
export type TaskingBotMemoryCategory = 'soul' | 'user' | 'memory';

export interface TaskingBotMemory {
  id: Generated<string>;
  userId: number;
  category: TaskingBotMemoryCategory;
  key: string;
  content: string;
  metadata: Generated<string>; // JSON object
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

// Cross-surface user memory system
export type UserMemoryCategory = 'preference' | 'fact' | 'pattern' | 'rejection' | 'workflow' | 'brand' | 'asset';
export type UserMemorySourceSurface = 'taskingbot' | 'ai_chat' | 'team_chat' | 'desktop' | 'extension' | 'api' | 'system';

export interface UserMemories {
  id: Generated<string>;
  userId: number;
  tenantId: string | null;
  category: UserMemoryCategory;
  key: string;
  content: string;
  tags: Generated<string>;          // JSON array of strings
  metadata: Generated<string>;      // JSON object
  sourceSurface: UserMemorySourceSurface | null;
  sourceSessionId: string | null;
  sourceConversationId: string | null;
  retrievalCount: Generated<number>;
  lastRetrievedAt: Timestamp | null;
  confidence: Generated<number>;    // 0.0-1.0
  isActive: Generated<number>;      // 0 or 1
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

// Task execution state for error recovery with checkpoints
export type TaskExecutionStatus = 'pending' | 'running' | 'checkpoint' | 'paused' | 'failed' | 'completed' | 'rolled_back';

export interface TaskExecutionState {
  id: Generated<string>;
  userId: number;
  sessionId: string | null;
  taskId: string | null;
  status: Generated<TaskExecutionStatus>;
  currentStep: Generated<number>;
  totalSteps: Generated<number>;
  stepsDefinition: Generated<string>;  // JSON array
  lastCheckpointStep: number | null;
  lastCheckpointState: string | null;  // JSON
  lastCheckpointAt: Timestamp | null;
  errors: Generated<string>;           // JSON array
  retryCount: Generated<number>;
  maxRetries: Generated<number>;
  lastToolCall: string | null;         // JSON
  diagnosis: string | null;            // JSON
  sourceSurface: UserMemorySourceSurface | null;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
  completedAt: Timestamp | null;
}

export interface UserSubscriptions {
  id: Generated<number>;
  userId: number;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  tier: Generated<SubscriptionTier>;
  status: Generated<SubscriptionStatus>;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  cancelAtPeriodEnd: Generated<number>;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

export interface SkillPacks {
  id: Generated<number>;
  userId: number;
  name: string;
  description: string | null;
  icon: Generated<string>;
  color: Generated<string>;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

export interface SkillPackSkills {
  id: Generated<number>;
  packId: number;
  skillName: string;
  skillFullName: string;
  skillDescription: string | null;
  skillUrl: string | null;
  position: Generated<number>;
  addedAt: Generated<Timestamp | null>;
}

export interface SkillPackAssignments {
  id: Generated<number>;
  packId: number;
  sessionId: string;
  personaId: string | null;
  assignedBy: number;
  assignedAt: Generated<Timestamp | null>;
}

export interface SessionRepositories {
  id: string;
  sessionId: string;
  repositoryFullName: string;
  branch: Generated<string>;
  defaultBranch: string | null;
  isPrimary: Generated<number>;
  prNumber: number | null;
  prUrl: string | null;
  prStatus: string | null;
  lastCommitSha: string | null;
  lastSyncedAt: string | null;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

export interface SessionTimeEntries {
  id: Generated<string>;
  sessionId: string;
  userId: number;
  category: Generated<string>;
  categoryColor: Generated<string>;
  todoId: string | null;
  note: Generated<string | null>;
  startTime: string;
  endTime: string;
  duration: number;
  createdAt: Generated<Timestamp | null>;
}

export interface TimeCategories {
  id: Generated<string>;
  userId: number;
  name: string;
  color: string;
  position: Generated<number | null>;
  createdAt: Generated<Timestamp | null>;
}

export interface SkillsRegistry {
  skillId: string;
  hash: string;
  status: string; // 'ok' | 'warn' | 'block'
  riskScore: number;
  findingsJson: string | null;
  adminOverride: string | null; // null | 'approved' | 'blocked'
  adminOverrideBy: string | null;
  adminOverrideAt: string | null;
  updatedAt: string;
}

export interface OpenclawSkills {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  category: string;
  githubUrl: string | null;
  skillMdContent: string | null;
  toolDefinition: string | null;
  isActive: number;
  installCount: number;
  lastSyncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UserOpenclawSkills {
  userId: number;
  skillId: string;
  isEnabled: number;
  config: string | null;
  enabledAt: string;
}

export interface SsoConfigurations {
  id: Generated<number>;
  emailDomain: string;
  provider: Generated<string>;
  displayName: string;
  clientId: string;
  clientSecret: string;
  issuerDomain: string;
  enabled: Generated<number>;
  configuredBy: number | null;
  createdAt: Generated<Timestamp | null>;
  updatedAt: Generated<Timestamp | null>;
}

// Multi-tenancy tables

export interface Partners {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  brandName: string | null;
  faviconUrl: string | null;
  primaryColor: Generated<string>;
  customDomain: string | null;
  customCss: string | null;
  themeId: string | null;
  setupCompleted: Generated<number>;
  contactEmail: string | null;
  billingEmail: string | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  partnerTier: Generated<PartnerTier>;
  wholesaleDiscountPct: Generated<number>;
  tenantsMax: Generated<number>;
  totalSeatsMax: Generated<number>;
  metadata: Generated<string>;
  isActive: Generated<number>;
  createdAt: Generated<string>;
  updatedAt: Generated<string>;
}

export interface Tenants {
  id: string;
  name: string;
  slug: string;
  type: Generated<TenantType>;
  partnerId: string | null;
  ownerUserId: number;
  logoUrl: string | null;
  brandName: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  customDomain: string | null;
  customCss: string | null;
  themeId: string | null;
  setupCompleted: Generated<number>;
  emailDomain: string | null;
  authMode: Generated<TenantAuthMode>;
  mfaRequired: Generated<number>;
  ssoConfigId: number | null;
  dlpPolicy: Generated<string>;
  metadata: Generated<string>;
  isActive: Generated<number>;
  createdAt: Generated<string>;
  updatedAt: Generated<string>;
}

export interface TenantUsers {
  id: Generated<number>;
  tenantId: string;
  userId: number;
  role: Generated<TenantUserRole>;
  invitedBy: number | null;
  joinedAt: Generated<string>;
  isActive: Generated<number>;
  createdAt: Generated<string>;
  updatedAt: Generated<string>;
}

export interface TenantSubscriptions {
  id: Generated<number>;
  tenantId: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  tier: Generated<SubscriptionTier>;
  status: Generated<SubscriptionStatus>;
  seatLimit: Generated<number>;
  seatCount: Generated<number>;
  workerLimit: Generated<number>;
  projectLimit: Generated<number>;
  storageLimitMb: Generated<number>;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  trialEnd: string | null;
  cancelAtPeriodEnd: Generated<number>;
  migratedFromUserSubscriptionId: number | null;
  createdAt: Generated<string>;
  updatedAt: Generated<string>;
}

export interface DlpPolicies {
  id: Generated<number>;
  tenantId: string | null;
  partnerId: string | null;
  name: string;
  description: string | null;
  policyJson: Generated<string>;
  isActive: Generated<number>;
  priority: Generated<number>;
  createdBy: number | null;
  createdAt: Generated<string>;
  updatedAt: Generated<string>;
}

export interface AuditLog {
  id: string;
  tenantId: string | null;
  partnerId: string | null;
  userId: number | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  details: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  severity: Generated<AuditSeverity>;
  createdAt: Generated<string>;
}

export type OrgThemeOwnerType = 'partner' | 'tenant';
export type CustomDomainRouteType = 'partner' | 'tenant';
export type SslStatus = 'pending' | 'active' | 'failed';

export interface OrgThemes {
  id: string;
  ownerType: OrgThemeOwnerType;
  ownerId: string;
  name: string;
  description: string | null;
  isActive: Generated<number>;
  tokens: Generated<string>;
  themeMode: Generated<string>;
  pairedThemeId: string | null;
  imagePrompt: string | null;
  imageUrl: string | null;
  imageGeneratedAt: string | null;
  createdBy: number | null;
  createdAt: Generated<string>;
  updatedAt: Generated<string>;
}

export interface CustomDomainRoutes {
  id: Generated<number>;
  domain: string;
  routeType: CustomDomainRouteType;
  routeId: string;
  sslStatus: Generated<SslStatus>;
  cloudflareCustomHostnameId: string | null;
  verifiedAt: string | null;
  createdAt: Generated<string>;
  updatedAt: Generated<string>;
}

// ── Lead/Sales Pipeline ──

export interface Leads {
  id: Generated<string>;
  userId: number;
  tenantId: number | null;
  // Contact info
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
  linkedinUrl: string | null;
  title: string | null;           // e.g. "VP of Engineering"
  // Company info
  companyName: string | null;
  companyDomain: string | null;
  companySize: string | null;     // e.g. "50-200"
  industry: string | null;
  techStack: string | null;       // JSON array of technologies
  fundingStage: string | null;    // e.g. "Series B"
  annualRevenue: string | null;
  location: string | null;
  // Pipeline
  status: Generated<LeadStatus>;
  priority: Generated<LeadPriority>;
  source: Generated<LeadSource>;
  pipelineId: string | null;
  stageOrder: Generated<number>;
  // Scoring
  icpScore: Generated<number>;    // 0-100 ICP match score
  engagementScore: Generated<number>;
  // Context
  notes: string | null;
  tags: string | null;            // JSON array of tags
  customFields: string | null;    // JSON object
  lastContactedAt: string | null;
  nextFollowUpAt: string | null;
  assignedTo: string | null;      // AI agent or user
  // Timestamps
  createdAt: Generated<string>;
  updatedAt: Generated<string>;
}

export interface LeadPipelines {
  id: Generated<string>;
  userId: number;
  tenantId: number | null;
  name: string;
  description: string | null;
  // ICP definition
  icpCriteria: string | null;     // JSON: { industries, titles, companySize, techStack, fundingStage, etc. }
  stages: string | null;          // JSON array of stage names
  isDefault: Generated<number>;
  // Agent config
  autoProspect: Generated<number>;
  prospectFrequency: string | null; // cron expression
  outreachEnabled: Generated<number>;
  // Stats cache
  totalLeads: Generated<number>;
  activeLeads: Generated<number>;
  wonDeals: Generated<number>;
  // Timestamps
  createdAt: Generated<string>;
  updatedAt: Generated<string>;
}

export interface LeadActivities {
  id: Generated<string>;
  leadId: string;
  userId: number;
  type: string;                   // 'note', 'email', 'call', 'meeting', 'status_change', 'score_update', 'outreach'
  channel: OutreachChannel | null;
  subject: string | null;
  content: string | null;
  metadata: string | null;        // JSON: { from, to, previousStatus, newStatus, etc. }
  outreachStatus: OutreachStatus | null;
  aiGenerated: Generated<number>;
  createdAt: Generated<string>;
}

export interface LeadOutreachSequences {
  id: Generated<string>;
  userId: number;
  pipelineId: string | null;
  name: string;
  description: string | null;
  channel: OutreachChannel;
  steps: string;                  // JSON array: [{ delay: "2d", template: "...", subject: "..." }]
  isActive: Generated<number>;
  totalSent: Generated<number>;
  totalReplied: Generated<number>;
  createdAt: Generated<string>;
  updatedAt: Generated<string>;
}

export interface JobSeekerProfiles {
  id: Generated<string>;
  userId: number;
  slug: string;
  headline: string | null;
  bio: string | null;
  skills: Generated<string>;
  experience: Generated<string>;
  education: Generated<string>;
  portfolioLinks: Generated<string>;
  phone: string | null;
  address: string | null;
  email: string | null;
  linkedinUrl: string | null;
  websiteUrl: string | null;
  resumeUrl: string | null;
  resumeFilename: string | null;
  availabilityStatus: Generated<string>;
  location: string | null;
  remotePreference: Generated<string>;
  hourlyRateMin: number | null;
  hourlyRateMax: number | null;
  rateCurrency: Generated<string>;
  isPublic: Generated<number>;
  createdAt: Generated<string>;
  updatedAt: Generated<string>;
}

export interface DB {
  aiChatMessages: AiChatMessages;
  aiPersonas: AiPersonas;
  aiTaskQueue: AiTaskQueue;
  annotations: Annotations;
  chatAttachments: ChatAttachments;
  checkpoints: Checkpoints;
  collaborationSessions: CollaborationSessions;
  filePermissions: FilePermissions;
  generatedImages: GeneratedImages;
  loginAttempts: LoginAttempts;
  n8nUserCredentials: N8nUserCredentials;
  n8nWorkflowExecutions: N8nWorkflowExecutions;
  n8nWorkflows: N8nWorkflows;
  oauthAccounts: OauthAccounts;
  oauthStates: OauthStates;
  personaImports: PersonaImports;
  platformSecrets: PlatformSecrets;
  publishedSites: PublishedSites;
  sessionAiParticipants: SessionAiParticipants;
  sessionAttachments: SessionAttachments;
  sessionChatMessages: SessionChatMessages;
  sessionFavorites: SessionFavorites;
  sessionFiles: SessionFiles;
  sessionInvitations: SessionInvitations;
  sessionMemorySummaries: SessionMemorySummaries;
  sessionParticipants: SessionParticipants;
  sessionPersonaSettings: SessionPersonaSettings;
  sessionPresence: SessionPresence;
  sessionRepositories: SessionRepositories;
  sessionSecrets: SessionSecrets;
  sessionThemes: SessionThemes;
  sessions: Sessions;
  userThemes: UserThemes;
  skillPacks: SkillPacks;
  skillPackSkills: SkillPackSkills;
  skillPackAssignments: SkillPackAssignments;
  taskingBotChannels: TaskingBotChannels;
  taskingBotCategories: TaskingBotCategories;
  taskingBotConversations: TaskingBotConversations;
  taskingBotProjects: TaskingBotProjects;
  taskingBotTasks: TaskingBotTasks;
  taskingBotChatMessages: TaskingBotChatMessages;
  taskingBotTaskMessages: TaskingBotTaskMessages;
  taskingBotMemory: TaskingBotMemory;
  sessionTimeEntries: SessionTimeEntries;
  taskQueues: TaskQueues;
  timeCategories: TimeCategories;
  todoItems: TodoItems;
  todoSteps: TodoSteps;
  userAiConfigs: UserAiConfigs;
  userIntegrations: UserIntegrations;
  userN8nIntegrations: UserN8nIntegrations;
  userMedia: UserMedia;
  userOpenclawSkills: UserOpenclawSkills;
  userPasswords: UserPasswords;
  userPersonaSettings: UserPersonaSettings;
  userSubscriptions: UserSubscriptions;
  users: Users;
  openclawSkills: OpenclawSkills;
  skillsRegistry: SkillsRegistry;
  ssoConfigurations: SsoConfigurations;
  contractorAgreements: ContractorAgreements;
  agreementSignatures: AgreementSignatures;
  agreementAuditLog: AgreementAuditLog;
  userSignatures: UserSignatures;
  messagingConnectors: MessagingConnectors;
  messagingInboundLog: MessagingInboundLog;
  partners: Partners;
  tenants: Tenants;
  tenantUsers: TenantUsers;
  tenantSubscriptions: TenantSubscriptions;
  dlpPolicies: DlpPolicies;
  auditLog: AuditLog;
  orgThemes: OrgThemes;
  customDomainRoutes: CustomDomainRoutes;
  leads: Leads;
  leadPipelines: LeadPipelines;
  leadActivities: LeadActivities;
  leadOutreachSequences: LeadOutreachSequences;
  jobSeekerProfiles: JobSeekerProfiles;
  userMemories: UserMemories;
  taskExecutionState: TaskExecutionState;
}

export interface ContractorAgreements {
  id: Generated<number>;
  sessionId: string;
  ownerUserId: number;
  participantUserId: number;
  agreementHtml: string;
  companyName: string | null;
  contractorName: string | null;
  effectiveDate: string | null;
  status: 'pending' | 'partially_signed' | 'fully_signed' | 'voided';
  createdAt: Generated<string>;
  updatedAt: Generated<string>;
}

export interface AgreementSignatures {
  id: Generated<number>;
  agreementId: number;
  userId: number;
  signerRole: 'company' | 'contractor';
  signatureImage: string;
  signerName: string;
  signerTitle: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  signedAt: Generated<string>;
}

export interface AgreementAuditLog {
  id: Generated<number>;
  agreementId: number;
  userId: number;
  action: string;
  details: string | null;
  ipAddress: string | null;
  createdAt: Generated<string>;
}

export interface UserSignatures {
  id: Generated<number>;
  userId: number;
  signatureImage: string;
  createdAt: Generated<string>;
}

export type MessagingPlatform = 'whatsapp' | 'telegram' | 'slack' | 'teams' | 'signal' | 'wechat' | 'discord';
export type MessagingConnectorStatus = 'pending_setup' | 'pending_verification' | 'connected' | 'disconnected' | 'error' | 'revoked';

export interface MessagingConnectors {
  id: Generated<string>;
  userId: number;
  platform: MessagingPlatform;
  label: string | null;
  enabled: number;
  config: string;
  verifyToken: string | null;
  webhookSecret: string | null;
  externalBotId: string | null;
  externalChatId: string | null;
  externalUserId: string | null;
  conversationId: string | null;
  status: MessagingConnectorStatus;
  lastError: string | null;
  lastMessageAt: string | null;
  lastWebhookAt: string | null;
  createdAt: Generated<string>;
  updatedAt: Generated<string>;
}

export interface MessagingInboundLog {
  id: Generated<string>;
  connectorId: string;
  platformMessageId: string;
  senderId: string | null;
  senderName: string | null;
  content: string | null;
  mediaUrl: string | null;
  processed: number;
  taskingbotConversationId: string | null;
  responseText: string | null;
  createdAt: Generated<string>;
}

// Use these for zod schema enums or other purposes, since the kysely enums are just types
export const SessionStatusArrayValues: [SessionStatus, ...SessionStatus[]] = [
  'active',
  'archived',
  'draft',
];
export const UserRoleArrayValues: [UserRole, ...UserRole[]] = ['admin', 'guest', 'host'];
export const SubscriptionTierArrayValues: [SubscriptionTier, ...SubscriptionTier[]] = [
  'free',
  'basic',
  'pro',
  'enterprise',
];
export const SubscriptionStatusArrayValues: [SubscriptionStatus, ...SubscriptionStatus[]] = [
  'active',
  'canceled',
  'past_due',
  'inactive',
  'trialing',
];
export const PermissionLevelArrayValues: [PermissionLevel, ...PermissionLevel[]] = [
  'edit',
  'none',
  'view',
];
export const AiProviderTypeArrayValues: [AiProviderType, ...AiProviderType[]] = [
  'anthropic',
  'google',
  'nvidia_nim',
  'ollama_cloud',
  'ollama_local',
  'openai',
  'openrouter',
];
export const IntegrationProviderArrayValues: [IntegrationProvider, ...IntegrationProvider[]] = [
  'atlassian',
  'github',
  'gitlab',
  'supabase',
];
export const QueuePriorityArrayValues: [QueuePriority, ...QueuePriority[]] = [
  'high',
  'low',
  'medium',
  'urgent',
];
export const AiTaskQueueStatusArrayValues: [AiTaskQueueStatus, ...AiTaskQueueStatus[]] = [
  'pending',
  'processing',
  'completed',
  'failed',
  'cancelled',
];
export const CheckpointTypeArrayValues: [CheckpointType, ...CheckpointType[]] = [
  'auto',
  'manual',
  'milestone',
];
export const UserPresenceStatusArrayValues: [UserPresenceStatus, ...UserPresenceStatus[]] = [
  'away',
  'meeting',
  'offline',
  'online',
];
export const DeploymentStatusArrayValues: [DeploymentStatus, ...DeploymentStatus[]] = [
  'deployed',
  'deploying',
  'failed',
  'pending',
];
/** Default built-in persona categories. Users can create additional ones. */
export const PersonaCategoryArrayValues: [string, ...string[]] = [
  'development',
  'security',
  'operations',
  'documentation',
  'compliance',
  'governance',
  'custom',
];

export const PersonaVisibilityArrayValues: [PersonaVisibility, ...PersonaVisibility[]] = [
  'private',
  'session',
  'public',
];

export const PersonaSourceArrayValues: [PersonaSource, ...PersonaSource[]] = [
  'manual',
  'imported',
  'ai_generated',
];

export const MemorySummaryTriggerArrayValues: [MemorySummaryTrigger, ...MemorySummaryTrigger[]] = [
  'auto',
  'manual',
  'deploy',
  'refactor',
  'migration',
];

export const N8nWorkflowExecutionStatusArrayValues: [
  N8nWorkflowExecutionStatus,
  ...N8nWorkflowExecutionStatus[],
] = ['running', 'success', 'error', 'cancelled'];

export const VoicePreferenceArrayValues: [VoicePreference, ...VoicePreference[]] = [
  'female_british',
  'male_deep',
];

export const SupportedLanguageCodeArrayValues: [SupportedLanguageCode, ...SupportedLanguageCode[]] =
  ['en', 'zh-CN', 'es', 'fr', 'de', 'it', 'pt-BR', 'ja', 'ko', 'ar'];
// Multi-tenancy enum arrays
export const TenantTypeArrayValues: [TenantType, ...TenantType[]] = ['personal', 'team', 'enterprise'];
export const TenantUserRoleArrayValues: [TenantUserRole, ...TenantUserRole[]] = [
  'owner',
  'admin',
  'member',
  'viewer',
  'billing',
];
export const TenantAuthModeArrayValues: [TenantAuthMode, ...TenantAuthMode[]] = [
  'password_only',
  'sso_required',
  'sso_optional',
];
export const PartnerTierArrayValues: [PartnerTier, ...PartnerTier[]] = ['silver', 'gold', 'platinum'];
export const AuditSeverityArrayValues: [AuditSeverity, ...AuditSeverity[]] = [
  'info',
  'warn',
  'error',
  'critical',
];

// Note: kysely maps the table/column/enum names from snake_case to camelCase and PascalCase. When running SQL statements, make sure to use snake_case, but TypeScript code should use camelCase or PascalCase.
